/**
 * Default values for Entity Formation Intake Form
 * All fields initialized with appropriate default values:
 * - strings = ""
 * - booleans = false
 * - option-based fields (YesNo, YesNoNotSure, YesNoLater, etc.) = null (unanswered)
 * - conditional fields (citizenship_status, etc.) = null (unanswered)
 * - multi-select arrays = []
 * - owners = [{ ... }]
 */

/**
 * Type definitions for option-based fields
 */
export type YesNo = 'yes' | 'no';
export type YesNoNotSure = 'yes' | 'no' | 'not_sure';
export type YesNoLater = 'yes' | 'no' | 'later';
export type OwnerType = 'individual' | 'entity' | 'trust';

export interface Owner {
  full_legal_name: string;
  ownership_percentage: string;
  owner_type: OwnerType | null;
  phone_number: string;
  email_address: string;
  street_address: string;
  street_address_2: string;
  city: string;
  state: string;
  zip_code: string;
  state_of_residence: string;
  us_citizen: YesNo | null;
  citizenship_status: string | null; // Required if us_citizen === 'no'
}

/**
 * Creates an empty Owner object with all fields initialized to default values.
 */
export function createEmptyOwner(): Owner {
  return {
    full_legal_name: '',
    ownership_percentage: '',
    owner_type: null,
    phone_number: '',
    email_address: '',
    street_address: '',
    street_address_2: '',
    city: '',
    state: '',
    zip_code: '',
    state_of_residence: '',
    us_citizen: null,
    citizenship_status: null, // Required if us_citizen === 'no'
  };
}

export interface EntityIntakeDefaults {
  // Section 1: Client Information
  full_legal_name: string;
  preferred_name: string;
  phone_number: string;
  email_address: string;
  street_address: string;
  street_address_2: string;
  city: string;
  state: string;
  zip_code: string;
  state_of_residence: string;
  us_citizen: YesNo | null;
  citizenship_status: string | null; // Required if us_citizen === 'no' (conditional)
  ssn_itin: string | null; // Optional - SSN/ITIN for IRS SS-4 form (will be deleted after EIN is received)
  
  // Section 2: Business Overview
  legal_business_name: string;
  alternate_business_names: string;
  business_description: string;
  industry: string[];
  industry_other: string | null; // Conditional if industry includes 'other'
  
  // Section 3: Business Goals & Vision
  timeline: string;
  revenue_expectations: string;
  growth_outlook: string;
  strategic_intent: string[];
  strategic_intent_other: string | null; // Conditional if strategic_intent includes 'other'
  
  // Section 4: Ownership & Management
  number_of_owners: string;
  owners: Owner[];
  management_preference: string;
  future_ownership_changes: YesNoNotSure | null;
  
  // Section 5: Location & Operations
  operating_states: string[];
  operates_outside_formation_state: YesNoNotSure | null;
  has_employees: YesNo | null;
  uses_contractors: YesNo | null;
  sells_online: YesNo | null;
  serves_nationally: YesNo | null;
  
  // Section 6: Risk & Compliance
  risk_factors: string[];
  liability_risk: string;
  
  // Section 7: Banking, Tax & Financial Setup
  has_business_bank_account: YesNo | null;
  uses_bookkeeping_software: YesNo | null;
  bookkeeping_software_name: string | null; // Conditional if uses_bookkeeping_software === 'yes'
  discussed_tax_planning: YesNo | null;
  
  // Section 8: Branding & Digital Presence
  owns_domain: YesNo | null;
  domain_name: string | null; // Conditional if owns_domain === 'yes'
  website_timeline: YesNoLater | null;
  interested_in_brand_protection: YesNoNotSure | null;
  
  // Section 9: Capital & Growth Planning
  initial_funding: string[];
  initial_funding_other: string | null;
  plans_to_raise_capital: YesNoNotSure | null;
  
  // Section 10: Additional Services Interest
  additional_services: string[];
  
  // Section 11: Acknowledgments
  acknowledge_not_attorney_or_cpa: boolean;
  acknowledge_entity_formation_scope: boolean;
  certify_accuracy: boolean;
  client_signature: string;
  signature_date: string;
}

export const ENTITY_INTAKE_DEFAULTS: EntityIntakeDefaults = {
  // Section 1: Client Information
  full_legal_name: '',
  preferred_name: '',
  phone_number: '',
  email_address: '',
  street_address: '',
  street_address_2: '',
  city: '',
  state: '',
  zip_code: '',
  state_of_residence: '',
  us_citizen: null,
  citizenship_status: null, // Required if us_citizen === 'no'
  ssn_itin: null, // Optional - SSN/ITIN for IRS SS-4 form
  
  // Section 2: Business Overview
  legal_business_name: '',
  alternate_business_names: '',
  business_description: '',
  industry: [],
  industry_other: null, // Conditional if industry includes 'other'
  
  // Section 3: Business Goals & Vision
  timeline: '',
  revenue_expectations: '',
  growth_outlook: '',
  strategic_intent: [],
  strategic_intent_other: null, // Conditional if strategic_intent includes 'other'
  
  // Section 4: Ownership & Management
  number_of_owners: '',
  owners: [createEmptyOwner()],
  management_preference: '',
  future_ownership_changes: null,
  
  // Section 5: Location & Operations
  operating_states: [],
  operates_outside_formation_state: null,
  has_employees: null,
  uses_contractors: null,
  sells_online: null,
  serves_nationally: null,
  
  // Section 6: Risk & Compliance
  risk_factors: [],
  liability_risk: '',
  
  // Section 7: Banking, Tax & Financial Setup
  has_business_bank_account: null,
  uses_bookkeeping_software: null,
  bookkeeping_software_name: null, // Conditional if uses_bookkeeping_software === 'yes'
  discussed_tax_planning: null,
  
  // Section 8: Branding & Digital Presence
  owns_domain: null,
  domain_name: null, // Conditional if owns_domain === 'yes'
  website_timeline: null,
  interested_in_brand_protection: null,
  
  // Section 9: Capital & Growth Planning
  initial_funding: [],
  initial_funding_other: null, // Conditional if initial_funding includes 'other'
  plans_to_raise_capital: null,
  
  // Section 10: Additional Services Interest
  additional_services: [],
  
  // Section 11: Acknowledgments
  acknowledge_not_attorney_or_cpa: false,
  acknowledge_entity_formation_scope: false,
  certify_accuracy: false,
  client_signature: '',
  signature_date: '',
};

